import { useState, useEffect, ChangeEvent } from 'react'
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Form,
  Label,
  FormFeedback,
  Row,
  Col,
  Spinner
  // @ts-ignore
} from 'reactstrap'
import { IconX } from '@tabler/icons-react'
import { useFetch } from '../../_hooks/useFetch'
import { usePost } from '../../_hooks/usePost'
import styled from 'styled-components'
// @ts-ignore
import classnames from 'classnames'
// @ts-ignore
import Select, { SelectOption } from 'react-select'
import { toast } from 'react-toastify'
import { selectThemeColors } from '../../utility/Utils'
import { Controller, useForm } from 'react-hook-form'
import mapper from '../../_endpoints'
// @ts-ignore
import { Button, Input } from 'dsm-react-lib'
import { StyledModalHeader } from './ContributorModal'

const ModalBodyContainer = styled(ModalBody)`
  padding: 0.8rem 3.2rem !important;
`

interface CreateCollectionModalProps {
  isOpen: boolean
  toggle: () => void
  onSubmittedCallback: (updatedCollection: any) => void
  collectionData: any
  isEditing: boolean
}

const CreateCollectionModal: React.FC<CreateCollectionModalProps> = ({
  isOpen,
  toggle,
  onSubmittedCallback,
  collectionData,
  isEditing = false
}) => {
  const { register, errors, handleSubmit, control, trigger } = useForm({})
  const initialApps =
    collectionData.applications?.length > 0
      ? collectionData.applications.map((app: any) => {
          return { label: app.name, value: app.id }
        })
      : undefined

  const [collectionName, setCollectionName] = useState(collectionData.collectionName)
  const [selectedApps, setSelectedApps] = useState(initialApps)
  const [collections, setCollections] = useState([])
  const [isDisabled, setIsDisabled] = useState(false)
  const [, getApplications] = useFetch()
  const [, getAppCollections] = useFetch()
  const [isSubmitting, submitCollection] = usePost()

  const [apps, setApps] = useState([])

  useEffect(() => {
    const getCollections = async () => {
      const collectionsResponse = await getAppCollections(mapper.appCollections.get)
      const options = collectionsResponse.map((collection: any) => {
        return {
          label: collection.collectionName,
          value: collection.collectionId
        }
      })

      setCollections(options)
    }

    const getAllApplications = async () => {
      const response = await getApplications(mapper.applications.get)
      const options = response
        .map((application: any) => {
          return {
            label: application.name,
            value: application.id
          }
        })
        .sort((a: any, b: any) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
      setApps(options)
    }
    getAllApplications()
    getCollections()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only fetch
  }, [])

  const validationMessages = {
    collectionName: 'Collection name must contain at least one alphanumeric character',
    collectionLength: 'Collection name is mandatory',
    apps: 'At least one application is required'
  }

  const checkCollectionNameValid = (name: string) => {
    return /[a-zA-Z]/.test(name) || /\d/.test(name)
  }

  const onCollectionNameChange = (name: string) => {
    setCollectionName(name)
  }

  const onSubmit = async () => {
    if (selectedApps === undefined) setSelectedApps([])
    const isValid = await trigger()
    if (isValid && selectedApps !== undefined && selectedApps?.length !== 0) {
      setIsDisabled(true)
      const data = {
        collectionId: collectionData.collectionId,
        collectionName,
        applications: selectedApps.map((application: any) => {
          return { id: application.value }
        })
      }
      const response = isEditing
        ? await submitCollection({
            url: mapper.appCollections.update,
            data,
            method: 'put'
          })
        : await submitCollection({
            url: mapper.appCollections.create,
            data
          })
      const updatedCollections = await getAppCollections(mapper.appCollections.get)

      if (response === 'Success') {
        onSubmittedCallback(updatedCollections)
      } else {
        toast.error(response)
        setIsDisabled(false)
      }
    }
  }

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} centered className="custom-modal-dialog">
        <StyledModalHeader
          toggle={toggle}
          data-testid="closeBtn"
          close={
            <IconX
              size={24}
              color="var(--icon-muted)"
              onClick={toggle}
              className="cursor-pointer"
            />
          }>
          {isEditing ? 'Edit Collection' : `Create a Collection`}
        </StyledModalHeader>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ModalBodyContainer>
            <>
              <Row>
                <div className="mb-1 col-md-12">
                  <Label className="form-label" for={`collectionName`}>
                    Collection name
                  </Label>
                  <Input
                    type="text"
                    id={`collectionName`}
                    name="collectionName"
                    placeholder="Productivity"
                    defaultValue={collectionName}
                    maxLength={50}
                    ref={register({
                      required: true,
                      validate: (v) => checkCollectionNameValid(v)
                    })}
                    invalid={'collectionName' in errors}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      onCollectionNameChange(e.target.value)
                    }
                    helperText={
                      'collectionName' in errors
                        ? collectionName?.length !== 0 && !checkCollectionNameValid(collectionName)
                          ? validationMessages.collectionName
                          : validationMessages.collectionLength
                        : ''
                    }
                    data-testid="collectionName"
                  />
                </div>
              </Row>
              <Row>
                <div className="mb-1 col-md-12" data-testid="applicationList">
                  <Label>Applications</Label>
                  <Controller
                    control={control}
                    name="apps"
                    render={() => (
                      <Select
                        maxMenuHeight={300}
                        isClearable={false}
                        isMulti
                        name="apps"
                        options={apps}
                        onChange={(selectedOption: SelectOption) => {
                          setSelectedApps(selectedOption)
                        }}
                        defaultValue={selectedApps}
                        classNamePrefix="select"
                        placeholder="Select applications"
                        theme={selectThemeColors}
                        className={classnames('react-select', {
                          'is-invalid': selectedApps?.length === 0
                        })}
                      />
                    )}
                  />
                  {selectedApps?.length === 0 && (
                    <FormFeedback invalid={1}>{validationMessages.apps}</FormFeedback>
                  )}
                </div>
              </Row>
            </>
          </ModalBodyContainer>
          <ModalFooter>
            <Button
              color="primary"
              className="mt-1"
              onClick={onSubmit}
              type="submit"
              disabled={isSubmitting || isDisabled}
              label={
                isSubmitting ? (
                  <>
                    Submitting... <Spinner />
                  </>
                ) : isEditing ? (
                  'Save collection'
                ) : (
                  'Create collection'
                )
              }
              data-testid="submitCollection"></Button>
          </ModalFooter>
        </Form>
      </Modal>
    </>
  )
}

export default CreateCollectionModal
